import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@mui/material";
import {
  SubmitHandler,
  useFieldArray,
  useForm,
  useWatch,
} from "react-hook-form";
import { Checkbox, InputText, UncontrolledSwitch } from "../../../formInputs/Inputs";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  FormDiv,
  SecondaryTitleText,
  FourthTitleText,
} from "../../../../helpers/generalStyles";
import Locker from "../../Locker";
import {
  ActivityPrices,
  activityPrices,
  upsertActivity as apiUpsertActivity,
} from "../../../../backend/api/activities";
import { useMutation } from "react-query";
import { queryClient } from "../../../..";
import AlertMessage from "../../../general/AlertMessage";
import React, { useEffect, useMemo, useState } from "react";
import {
  checkLastPriceName,
  isArrayEmpty,
  Message,
  toastEmitter,
} from "../../../../helpers/helpers";
import {
  ActivityPricesStatusEnum,
  ActivityPricesTypeEnum,
} from "helpers/constants";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import { ACTIVITY_SAVE_STATE_UPDATE } from "constants/activityConstants";
import { Container } from "../../style";
import "./PricesForm.css";
import { maxPriceTiers } from "./PricesFormConstants";
import { HtmlTooltip } from "../hoursForm/HoursForm";
import InfoIcon from "@mui/icons-material/Info";
import {
  areLastPricesTiersCapacityValid,
  checkAndEditPriceTiersMinQuantity,
} from "./PriceForm.utils";

export function PricesForm(props: {
  inputData: ActivityPrices;
  activityId: number | null;
  isLockerOpen: boolean;
  setIsLockerOpen: any;
  navigate: any;
  t: any;
  isGroupActivity: boolean;
  priceTierMaxCapacity: number;
}) {
  const {
    inputData,
    activityId,
    isLockerOpen,
    setIsLockerOpen,
    t,
    isGroupActivity,
    priceTierMaxCapacity,
  } = { ...props };
  const [alertMessage, setAlertMessage] = useState<Message>({
    type: "success",
    message: "",
  });
  const dispatch = useDispatch();
  const { saveActivityInProgress } = useSelector(
    (state: RootState) => state.activity,
  );

  useEffect(() => {
    setAlertMessage({ message: "", type: "success" });
  }, [inputData]);

  const isAddPriceActive = useMemo(
    () => !isGroupActivity || inputData.prices.length === 0,
    [inputData, isGroupActivity],
  );

  const mutateActivity = useMutation({
    mutationFn: (data: ActivityPrices) =>
      apiUpsertActivity({
        ...data,
        activityId: activityId ? String(activityId) : null,
      }),
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({
        queryKey: ["getActivitiesWithDisabled"],
      });
      setIsLockerOpen(false);
      toastEmitter(t("saveActivityPricesSuccessfully"), "success");
      dispatch({ type: ACTIVITY_SAVE_STATE_UPDATE, prices: true });
    },
    onError: (error: any) => {
      console.log(error);
      setAlertMessage({
        type: "error",
        message: t(error?.response?.data?.error),
      });
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(activityPrices),
    values: inputData,
  });

  const onSubmit: SubmitHandler<any> = (data: ActivityPrices) => {
    const doesNewPriceNameExists = checkLastPriceName(data.prices);
    if (doesNewPriceNameExists)
      toastEmitter(t("priceNameAlreadyExists"), "error");
    else if (
      !areLastPricesTiersCapacityValid(data.prices, priceTierMaxCapacity)
    )
      toastEmitter(t("lastPriceMaxQuantity"), "error");
    else mutateActivity.mutate(data);
  };

  const { fields, append, update } = useFieldArray({
    control,
    name: "prices",
    keyName: "_id",
  });

  const setPriceStatusToDELETED = (index: number) => {
    update(index, {
      ...watch().prices[index],
      status: ActivityPricesStatusEnum.DELETED,
    });
  };

  useEffect(() => {
    dispatch({
      type: ACTIVITY_SAVE_STATE_UPDATE,
      payload: { prices: !isDirty },
    });

    if (saveActivityInProgress && isDirty) {
      const formData = getValues();
      onSubmit(formData);
    }
  }, [saveActivityInProgress, isDirty]);

  const [currentPrices] = useWatch({
    control,
    name: ["prices"],
  });

  useEffect(() => {
    currentPrices.map((currentPrice, priceIndex) => {
      const { tiers } = currentPrice;
      if (tiers?.length) {
        checkAndEditPriceTiersMinQuantity(tiers, setValue, priceIndex);
      }
    });
  }, [currentPrices]);

  const addTier = (priceIndex: number) => {
    const priceTiers = currentPrices[priceIndex]?.tiers ?? [];

    if (priceTiers.length >= maxPriceTiers) {
      toastEmitter(t("maxTiersReached"), "error");
      return;
    }
    let newTier = {
      minQuantity: 1,
      maxQuantity: priceTierMaxCapacity,
      price: 0,
    };
    if (priceTiers.length > 0) {
      const newTierMinQuantity =
        priceTiers[priceTiers.length - 1].maxQuantity + 1;
      if (newTierMinQuantity > priceTierMaxCapacity) {
        return toastEmitter(t("maxTierCapacityReached"), "error");
      }

      newTier.minQuantity = newTierMinQuantity;
    }
    priceTiers.push(newTier);
    setValue(`prices.${priceIndex}.tiers`, priceTiers);
  };

  const removeLastTier = (priceIndex: number, tierIndex: number) => {
    const priceTiers = currentPrices[priceIndex]?.tiers ?? [];
    if (!isArrayEmpty(priceTiers) && priceTiers.length - 1 === tierIndex) {
      let newPriceTiers;
      if (priceTiers.length > 1) {
        newPriceTiers = priceTiers.slice(0, -1);
        newPriceTiers[newPriceTiers.length - 1].maxQuantity =
          priceTierMaxCapacity;
      }
      setValue(`prices.${priceIndex}.tiers`, newPriceTiers);
    }
  };

  const toggleTieredPricing = (index: number) => {
    currentPrices[index].tieredPricingEnabled =
      !currentPrices[index].tieredPricingEnabled;
    if (!currentPrices[index].tieredPricingEnabled) {
      currentPrices[index].tiers = [];
    }
    update(index, currentPrices[index]);
  };

  const prices = watch("prices") || [];

  return (
    <Container>
      <Locker
        isOpen={isLockerOpen}
        onClick={() => setIsLockerOpen(!isLockerOpen)}
      />
      <SecondaryTitleText>
        {t("pricesSubTitle").toUpperCase()}
      </SecondaryTitleText>
      {/* cause FormDiv have minWidth: 400px and it's too much for here */}
      <FormDiv style={{ minWidth: "200px" }} onSubmit={handleSubmit(onSubmit, (errors) => console.error("Form errors:", errors))}>
        <AlertMessage
          alertMessage={alertMessage.message}
          setAlertMessage={setAlertMessage}
          type={alertMessage.type}
          t={t}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            borderBottom: "solid 1px #f2f2f2",
            alignItems: "center",
            gap: "20px",
            maxHeight: "calc(100vh - 300px)",
            width: "100%",
            overflowY: "scroll",
            paddingTop: "10px",
          }}
        >
          {fields.map((field: any, index) => {
            const isPriceEnabled =
              field.status === ActivityPricesStatusEnum.ENABLED;
            const isPriceDisabled =
              field.status === ActivityPricesStatusEnum.DISABLED;
            const hasPriceNullType = !Boolean(field.type);
            const isPriceDeleted =
              field.status === ActivityPricesStatusEnum.DELETED;
            const currentPrice = prices[index] || {};
            const tiers = currentPrice.tiers ?? [];
            const hasSpecialDates = prices[index]?.hasSpecialDates;

            return (
              <React.Fragment key={index}>
                {!isPriceDeleted && (
                  <div
                    key={field._id}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: "20px",
                      width: "100%",
                      borderBottom: "solid lightGrey 1px",
                      borderRadius: "10px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        width: "100%",
                      }}
                    >
                      {currentPrice.type ? (
                        <>
                          <div className="price-type-container ">
                            <UncontrolledSwitch
                              key={index}
                              isActive={isPriceEnabled}
                              onChange={() =>
                                isPriceEnabled
                                  ? update(index, {
                                    ...field,
                                    id: Number(field.id),
                                    status: ActivityPricesStatusEnum.DISABLED,
                                  })
                                  : update(index, {
                                    ...field,
                                    id: Number(field.id),
                                    status: ActivityPricesStatusEnum.ENABLED,
                                  })
                              }
                              disabled={!isLockerOpen}
                              t={t}
                            />
                            <FourthTitleText style={{ margin: "0px" }}>
                              {t(`priceName.${currentPrice.type}`)}
                            </FourthTitleText>
                          </div>
                          <div style={{ color: "gray", fontWeight: "100" }}>
                            {currentPrice.type ===
                              ActivityPricesTypeEnum.DEFAULT
                              ? t("mandatoryPriceTypeSubTitle")
                              : t("optionnalPriceTypeSubTitle")}
                          </div>
                          <div className="price-special-date-activation-checkbox">
                            <Checkbox
                              id={`prices.${index}.hasSpecialDates`}
                              name={`prices.${index}.hasSpecialDates`}
                              control={control}
                              uppercase={false}
                              labelClassName="price-special-date-checkbox-label"
                              label={
                                <>
                                  {t("specialDatesCheckboxLabel")} <u>{t("specialDatesCheckboxLabelUnderLined")}</u>
                                </>
                              }
                              disabled={!isLockerOpen || isPriceDisabled}
                              t={t}
                            />
                            {hasSpecialDates && (
                              <div className="price-special-dates-container">
                                <InputText
                                  id={`prices.${index}.startDate`}
                                  name={`prices.${index}.startDate`}
                                  inputType="date"
                                  required={hasSpecialDates}
                                  InputLabelProps={{ shrink: true }}
                                  label={t("specialPriceStartDate")}
                                  disabled={!isLockerOpen}
                                  control={control}
                                  error={
                                    t(errors?.prices?.[index]?.startDate?.message) ||
                                    null
                                  }
                                  t={t}
                                />
                                <InputText
                                  id={`prices.${index}.endDate`}
                                  name={`prices.${index}.endDate`}
                                  inputType="date"
                                  required={hasSpecialDates}
                                  InputLabelProps={{ shrink: true }}
                                  label={t("specialPriceEndDate")}
                                  disabled={!isLockerOpen}
                                  control={control}
                                  error={
                                    t(errors?.prices?.[index]?.endDate?.message) ||
                                    null
                                  }
                                  t={t}
                                />
                              </div>
                            )}
                          </div>
                        </>
                      ) :
                        <><DeleteIcon
                          style={{
                            color: isLockerOpen ? "#F73131" : "#D3D3D3",
                            cursor: "pointer",
                            alignSelf: "self-end",
                            marginRight: "5px",
                          }}
                          onClick={() => {
                            setPriceStatusToDELETED(index);
                          }} />
                          <div className="price-special-date-activation-checkbox">
                            <Checkbox
                              id={`prices.${index}.hasSpecialDates`}
                              name={`prices.${index}.hasSpecialDates`}
                              control={control}
                              uppercase={false}
                              labelClassName="price-special-date-checkbox-label"
                              label={<>
                                {t("specialDatesCheckboxLabel")} <u>{t("specialDatesCheckboxLabelUnderLined")}</u>
                              </>}
                              disabled={!isLockerOpen || isPriceDisabled}
                              t={t} />
                            {hasSpecialDates && (
                              <div className="price-special-date-activation-checkbox">
                                {hasSpecialDates && (
                                  <div className="price-special-dates-container">
                                    <InputText
                                      id={`prices.${index}.startDate`}
                                      name={`prices.${index}.startDate`}
                                      inputType="date"
                                      required={hasSpecialDates}
                                      InputLabelProps={{ shrink: true }}
                                      label={t("specialPriceStartDate")}
                                      disabled={!isLockerOpen}
                                      control={control}
                                      error={
                                        t(errors?.prices?.[index]?.startDate?.message) ||
                                        null
                                      }
                                      t={t}
                                    />
                                    <InputText
                                      id={`prices.${index}.endDate`}
                                      name={`prices.${index}.endDate`}
                                      inputType="date"
                                      required={hasSpecialDates}
                                      InputLabelProps={{ shrink: true }}
                                      label={t("specialPriceEndDate")}
                                      disabled={!isLockerOpen}
                                      control={control}
                                      error={
                                        t(errors?.prices?.[index]?.endDate?.message) ||
                                        null
                                      }
                                      t={t}
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          </>}
                    </div>

                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "100%",
                      }}
                    >
                      <div style={{ width: "60%" }}>
                        <InputText
                          hide={currentPrice.type ? true : false}
                          id={`prices.${index}.name`}
                          name={`prices.${index}.name`}
                          label={t("priceLabel")}
                          required
                          disabled={!isLockerOpen || isPriceDisabled}
                          control={control}
                          error={
                            errors.prices
                              ? errors?.prices[index]?.name?.message
                              : null
                          }
                          t={t}
                        />
                        <InputText
                          id={`prices.${index}.price`}
                          name={`prices.${index}.price`}
                          label={t("price")}
                          inputType={"decimal"}
                          required
                          disabled={!isLockerOpen || isPriceDisabled}
                          control={control}
                          error={
                            errors.prices
                              ? errors?.prices[index]?.price?.message
                              : null
                          }
                          t={t}
                        />
                        <InputText
                          id={`prices.${index}.description`}
                          name={`prices.${index}.description`}
                          label={t("description")}
                          disabled={!isLockerOpen || isPriceDisabled}
                          control={control}
                          error={
                            errors.prices
                              ? errors?.prices[index]?.description?.message
                              : null
                          }
                          t={t}
                        />
                      </div>
                    </div>
                    <div className="tiered-prices-activation-container">
                      <UncontrolledSwitch
                        key={index}
                        isActive={field.tieredPricingEnabled}
                        onChange={() => toggleTieredPricing(index)}
                        disabled={!isLockerOpen}
                        t={t}
                      />
                      <span className="price-activation-switch-label">
                        {t("activateTieredPricing")}
                      </span>
                      <HtmlTooltip title={t("tieredPricingInfo")}>
                        <InfoIcon color="primary" />
                      </HtmlTooltip>
                    </div>
                    {!isArrayEmpty(tiers) && field.tieredPricingEnabled && (
                      <React.Fragment key={index}>
                        {tiers.map((tier: any, tierIndex: number) => (
                          <div key={tierIndex} className="tiers-container">
                            <div className="tier-container">
                              <span className="tier-label">
                                {t("tier")} {tierIndex + 1}
                              </span>
                              <InputText
                                name={`prices.${index}.tiers.${tierIndex}.minQuantity`}
                                id={`prices.${index}.tiers.${tierIndex}.minQuantity`}
                                label="Minimum"
                                inputType="number"
                                required
                                disabled={true}
                                control={control}
                                error={
                                  errors.prices
                                    ? errors?.prices[index]?.price?.message
                                    : null
                                }
                                t={t}
                              />
                              <InputText
                                name={`prices.${index}.tiers.${tierIndex}.maxQuantity`}
                                id={`prices.${index}.tiers.${tierIndex}.maxQuantity`}
                                label="Maximum"
                                inputType="number"
                                required
                                disabled={!isLockerOpen}
                                control={control}
                                error={
                                  errors.prices
                                    ? errors?.prices[index]?.price?.message
                                    : null
                                }
                                t={t}
                                max={priceTierMaxCapacity}
                                min={tier.minQuantity}
                              />
                              <InputText
                                name={`prices.${index}.tiers.${tierIndex}.price`}
                                id={`prices.${index}.tiers.${tierIndex}.price`}
                                label={t("price")}
                                inputType="decimal"
                                required
                                disabled={!isLockerOpen}
                                control={control}
                                error={
                                  errors.prices
                                    ? errors?.prices[index]?.price?.message
                                    : null
                                }
                                t={t}
                                min={0}
                              />
                              {tierIndex === tiers.length - 1 && (
                                <DeleteIcon
                                  style={{
                                    color: isLockerOpen ? "#F73131" : "#D3D3D3",
                                    cursor: "pointer",
                                  }}
                                  onClick={() => {
                                    removeLastTier(index, tierIndex);
                                  }}
                                />
                              )}
                            </div>
                          </div>
                        ))}
                      </React.Fragment>
                    )}
                    {field.tieredPricingEnabled && (
                      <div className="add-tier-container ">
                        <AddCircleOutlineIcon
                          style={{
                            color: isLockerOpen ? "#4FD1C5" : "#D3D3D3",
                            cursor: "pointer",
                          }}
                          onClick={() => {
                            addTier(index);
                          }}
                        />
                        <span>{t("addNewPriceTier")}</span>
                      </div>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
          {isAddPriceActive && (
            <AddCircleOutlineIcon
              style={{
                color: isLockerOpen ? "#4FD1C5" : "#D3D3D3",
                cursor: "pointer",
              }}
              onClick={() =>
                isLockerOpen
                  ? append({
                    name: "",
                    price: 1,
                    description: null,
                    status: ActivityPricesStatusEnum.ENABLED,
                    tieredPricingEnabled: false,
                    tiers: [],
                    hasSpecialDates: false,
                    startDate: null,
                    endDate: null,
                  })
                  : console.log("locker clos")
              }
            />
          )}
        </div>
        <Button
          disabled={!isLockerOpen || mutateActivity.isLoading}
          color="info"
          variant="contained"
          type="submit"
        >
          {t("save")}
        </Button>
      </FormDiv>
    </Container>
  );
}
